import { useQuery } from "@tanstack/react-query";
import { DropletIcon } from "lucide-react";
import { Link, usePath } from "raviger";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { AllergyIcon } from "@/CAREUI/icons/CustomIcons";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import query from "@/Utils/request/query";
import { formatTruncatedList } from "@/Utils/utils";
import { useEncounter } from "@/pages/Encounters/utils/EncounterProvider";
import allergyIntoleranceApi from "@/types/emr/allergyIntolerance/allergyIntoleranceApi";
import { completedEncounterStatus } from "@/types/emr/encounter/encounter";

export const ClinicalHistoryOverview = (props: React.ComponentProps<"div">) => {
  const { t } = useTranslation();
  const { facilityId, patientId, patient, primaryEncounter } = useEncounter();

  const { data: allergies } = useQuery({
    queryKey: ["allergies", patientId, "confirmed"],
    queryFn: query(allergyIntoleranceApi.getAllergy, {
      pathParams: { patientId },
      queryParams: {
        verification_status: "confirmed",
      },
    }),
    enabled:
      primaryEncounter &&
      !completedEncounterStatus.includes(primaryEncounter.status),
  });

  const sourceUrl = usePath();

  return (
    <div
      {...props}
      className={cn(
        "bg-white rounded-lg p-4 border border-gray-200 dark:bg-gray-950 dark:border-gray-800",
        props.className,
      )}
    >
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div className="flex flex-row flex-wrap items-start gap-4">
          <div className="flex flex-col items-start gap-1 whitespace-nowrap">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {t("blood_group")}:
            </span>
            <Badge variant="destructive">
              <DropletIcon className="size-4" strokeWidth={1.5} />
              <span>
                {t(`BLOOD_GROUP_LONG__${patient?.blood_group || "unknown"}`)}
              </span>
            </Badge>
          </div>

          {!!allergies?.results.length && (
            <div className="flex flex-col items-start gap-1 max-w-xs">
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {t("allergies")}:
              </span>
              <Badge
                variant="yellow"
                className="items-start gap-1.5 whitespace-normal leading-tight py-1.5"
              >
                <AllergyIcon className="size-4 shrink-0 mt-0.5" />
                <span className="break-words">
                  {formatTruncatedList(
                    allergies?.results || [],
                    2,
                    (allergy) => allergy.code.display,
                  )}
                </span>
              </Badge>
            </div>
          )}
        </div>

        <Button
          asChild
          variant="outline"
          size="lg"
          className="md:w-auto w-full shrink-0"
        >
          <Link
            href={
              facilityId
                ? `/facility/${facilityId}/patient/${patientId}/history/symptoms?sourceUrl=${encodeURIComponent(sourceUrl ?? "")}`
                : `/patient/${patientId}/history/symptoms?sourceUrl=${encodeURIComponent(sourceUrl ?? "")}`
            }
          >
            <img
              src="/images/icons/clinical_history.svg"
              alt="Clinical History"
              className="size-4"
            />
            {t("see_clinical_history")}
          </Link>
        </Button>
      </div>
    </div>
  );
};
